import React, { useState } from "react";
import {
  Info,
  AlertTriangle,
  CheckCircle,
  Code,
  Quote,
  Lightbulb,
  Check,
  Square,
  CheckSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { parseAIAssistantContent } from "@/utils/aiAssistantRichContentParser";
import { AI_ASSISTANT_RICH_CONTENT_TAGS } from "@/config/aiAssistantRichContent";
import DOMPurify from "dompurify";

interface AIRichContentProps {
  content: string;
  className?: string;
}

/**
 * Process basic formatting like bold, italic, links for nested content
 */
const processBasicFormatting = (text: string): string => {
  if (!text) return "";

  // Normalize escaped newlines - content from DB has literal \n characters
  let formatted = text.split("\\n").join("\n"); // Convert literal \n to actual newlines

  // First, handle HTML tags (must be done before markdown processing)
  formatted = formatted
    // Process HTML bold tags (including nested content)
    .replace(
      /<strong>(.*?)<\/strong>/gis,
      "<strong class='font-semibold'>$1</strong>",
    )
    .replace(/<b>(.*?)<\/b>/gis, "<strong class='font-semibold'>$1</strong>")
    // Process HTML italic tags
    .replace(/<em>(.*?)<\/em>/gis, "<em class='italic'>$1</em>")
    .replace(/<i>(.*?)<\/i>/gis, "<em class='italic'>$1</em>");

  // Markdown-style headings (after HTML to avoid conflicts)
  formatted = formatted
    // Emit clean heading tags; spacing handled by CSS via .ai-content-text rules
    .replace(/^### (.*$)/gm, "<h3>$1</h3>")
    .replace(/^## (.*$)/gm, "<h2>$1</h2>")
    .replace(/^# (.*$)/gm, "<h1>$1</h1>");

  // Markdown inline formatting (only if not already HTML formatted)
  formatted = formatted
    // Markdown bold (but not if already in HTML tags)
    .replace(
      /(?<!<[^>]*?)\*\*([^*]+?)\*\*(?![^<]*?>)/g,
      "<strong class='font-semibold'>$1</strong>",
    )
    // Markdown italic (but not if already in HTML tags)
    .replace(
      /(?<!<[^>]*?)\*([^*]+?)\*(?![^<]*?>)/g,
      "<em class='italic'>$1</em>",
    )
    // Links
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="text-blue-600 hover:underline hover:text-blue-800 transition-colors" target="_blank" rel="noopener noreferrer">$1</a>',
    );

  // Respect paragraph breaks (two or more newlines) and soft breaks
  // First expand paragraph breaks
  formatted = formatted.replace(/\n{2,}/g, "<br><br>");
  // Then convert remaining single newlines to <br>, avoiding duplicates around block elements
  formatted = formatted.replace(
    /(?<!<\/(?:h[1-6]|p|li|ul|ol|div)>)\n(?!\n|<(?:h[1-6]|p|li|ul|div)>)/g,
    "<br>",
  );

  // Sanitize but allow list and heading tags so HTML lists render correctly
  return DOMPurify.sanitize(formatted, {
    ALLOWED_TAGS: [
      "strong",
      "em",
      "a",
      "ul",
      "ol",
      "li",
      "br",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "span",
      "b",
      "i",
    ],
    ALLOWED_ATTR: ["class", "href", "target", "rel"],
    ALLOW_DATA_ATTR: false,
  });
};

/**
 * Helper component that recursively renders content with nested rich content tags
 */
const NestedContentRenderer: React.FC<{ content: string }> = ({ content }) => {
  if (!content) return null;

  // Check if content contains any rich content tags
  const hasRichContent = Object.values(AI_ASSISTANT_RICH_CONTENT_TAGS).some(
    (config) => config.regex.test(content),
  );

  if (!hasRichContent) {
    // No custom rich tags, but still apply basic formatting (bold/italic/links/line breaks)
    // Suppress stray newlines at boundaries
    const processed = processBasicFormatting(content.replace(/^\n+|\n+$/g, ""));
    return (
      <div
        className="ai-content-text prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: processed }}
      />
    );
  }

  // Parse and render with rich content
  const parsed = parseAIAssistantContent(content);

  // Build elements with pairing: task + following checklist = nested checklist under task
  const elements: React.ReactNode[] = [];
  for (let i = 0; i < parsed.segments.length; i++) {
    const segment = parsed.segments[i];

    if (segment.type === "text") {
      const nextSegment = parsed.segments[i + 1];
      const isFollowedByRich = nextSegment && nextSegment.type === "rich";
      // Trim boundary newlines when adjacent to rich blocks
      const boundaryTrimmed = isFollowedByRich
        ? segment.content.replace(/\n+$/g, "")
        : segment.content;
      const prev = parsed.segments[i - 1];
      const boundaryFinal =
        prev && prev.type === "rich"
          ? boundaryTrimmed.replace(/^\n+/g, "")
          : boundaryTrimmed;
      const processedContent = processBasicFormatting(boundaryFinal);
      elements.push(
        <div
          key={`text-${i}`}
          className={cn(
            "ai-content-text prose prose-sm max-w-none",
            isFollowedByRich && "mb-2",
          )}
          dangerouslySetInnerHTML={{ __html: processedContent }}
        />,
      );
      continue;
    }

    if (segment.type === "rich" && segment.richType === "task") {
      const next = parsed.segments[i + 1];
      // If the next segment is a checklist, render as subtasks under the task
      if (next && next.type === "rich" && next.richType === "checklist") {
        elements.push(
          <div key={`task-group-${i}`} className="my-2">
            <AITask content={segment.content} />
            <div className="ml-8 mt-1">
              <AIChecklistGroup content={next.content} />
            </div>
          </div>,
        );
        i++; // skip checklist, already rendered as subtasks
        continue;
      }

      // Fallback to regular task rendering
      elements.push(
        <LazyRichComponent
          key={`task-${i}`}
          richType={segment.richType}
          content={segment.content}
          className={segment.className}
        />,
      );
      continue;
    }

    if (segment.type === "rich" && segment.richType) {
      elements.push(
        <LazyRichComponent
          key={`rich-${i}`}
          richType={segment.richType}
          content={segment.content}
          className={segment.className}
        />,
      );
      continue;
    }
  }

  return <>{elements}</>;
};

/**
 * Lazy component resolver to avoid circular dependencies
 */
const LazyRichComponent: React.FC<{
  richType: string;
  content: string;
  className?: string;
}> = ({ richType, content, className }) => {
  // Import the component map dynamically to avoid circular deps
  const config =
    AI_ASSISTANT_RICH_CONTENT_TAGS[
      richType as keyof typeof AI_ASSISTANT_RICH_CONTENT_TAGS
    ];

  if (!config) return <span>{content}</span>;

  // Map component names to actual components
  const componentMap = {
    AIInfoBox,
    AIHighlight,
    AIActionItem,
    AIWarning,
    AICodeBlock,
    AIQuote,
    AICallout,
    AITask,
    AIChecklistItem,
    AIChecklistGroup,
    AIColorPreview,
    AITable,
    AIUnorderedList,
    AIOrderedList,
  };

  const ComponentName = config.component as keyof typeof componentMap;
  const Component = componentMap[ComponentName];

  if (!Component) return <span>{content}</span>;

  return <Component content={content} className={className} />;
};

// Info Box Component
export const AIInfoBox: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => (
  <div
    className={cn(
      "bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg mt-2 mb-6",
      className,
    )}
  >
    <div className="flex items-start gap-3">
      <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
      <div className="text-blue-800 font-medium leading-relaxed">
        <NestedContentRenderer content={content} />
      </div>
    </div>
  </div>
);

// Highlight Component
export const AIHighlight: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => (
  <span
    className={cn(
      "bg-yellow-100 px-2 py-1 rounded font-medium text-yellow-800",
      className,
    )}
  >
    {content}
  </span>
);

// Action Item Component
export const AIActionItem: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => (
  <div
    className={cn(
      "bg-green-50 border border-green-200 p-6 rounded-lg mt-2 mb-6",
      className,
    )}
  >
    <div className="flex items-start gap-3">
      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
      <div className="text-green-800 font-medium leading-relaxed">
        <NestedContentRenderer content={content} />
      </div>
    </div>
  </div>
);

// Warning Component
export const AIWarning: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => (
  <div
    className={cn(
      "bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg mt-2 mb-6",
      className,
    )}
  >
    <div className="flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
      <div className="text-red-700 font-medium leading-relaxed">
        <NestedContentRenderer content={content} />
      </div>
    </div>
  </div>
);

// Code Block Component
export const AICodeBlock: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => (
  <div className={cn("bg-gray-100 border rounded-lg p-6 mt-2 mb-6", className)}>
    <div className="flex items-start gap-3">
      <Code className="h-5 w-5 text-gray-600 mt-0.5 flex-shrink-0" />
      <pre className="font-mono text-sm text-gray-800 overflow-x-auto whitespace-pre-wrap">
        {content}
      </pre>
    </div>
  </div>
);

// Quote Component
export const AIQuote: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => (
  <div
    className={cn("border-l-4 border-gray-300 pl-4 py-2 mt-2 mb-6", className)}
  >
    <div className="flex items-start gap-3">
      <Quote className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
      <div className="italic text-gray-600 leading-relaxed">{content}</div>
    </div>
  </div>
);

// Callout Component
export const AICallout: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => (
  <div
    className={cn(
      "bg-purple-50 border border-purple-200 p-6 rounded-lg mt-2 mb-6",
      className,
    )}
  >
    <div className="flex items-start gap-3">
      <Lightbulb className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
      <div className="text-purple-800 font-medium leading-relaxed">
        <NestedContentRenderer content={content} />
      </div>
    </div>
  </div>
);

// Task Component
export const AITask: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => {
  const [isCompleted, setIsCompleted] = useState(false);

  return (
    <div className={cn(className, "mt-2 mb-6")}>
      <div className="flex items-start gap-3">
        <button
          onClick={() => setIsCompleted(!isCompleted)}
          className="flex-shrink-0 transition-colors hover:bg-slate-100 rounded p-1"
        >
          {isCompleted ? (
            <CheckSquare className="h-4 w-4 text-green-600" />
          ) : (
            <Square className="h-4 w-4 text-slate-400" />
          )}
        </button>
        <div
          className={cn(
            "text-slate-700 text-sm font-medium",
            isCompleted && "line-through text-slate-500",
          )}
        >
          <NestedContentRenderer content={content} />
        </div>
      </div>
    </div>
  );
};

// Checklist Item Component (individual item)
export const AIChecklistItem: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => {
  const [isChecked, setIsChecked] = useState(false);

  return (
    <div className={cn(className, "flex items-center gap-2 py-1")}>
      <button
        onClick={() => setIsChecked(!isChecked)}
        className="flex-shrink-0 transition-colors hover:bg-slate-100 rounded p-1"
      >
        {isChecked ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <Square className="h-4 w-4 text-slate-400" />
        )}
      </button>
      <span
        className={cn(
          "text-slate-700 text-sm",
          isChecked && "line-through text-slate-500",
        )}
        dangerouslySetInnerHTML={{ __html: processBasicFormatting(content) }}
      />
    </div>
  );
};

// Checklist Group Component (handles multiple items)
export const AIChecklistGroup: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => {
  // Split items by | separator
  const items = content
    .split("|")
    .map((item) => item.trim())
    .filter((item) => item);

  return (
    <div className={cn(className)}>
      {items.map((item, index) => (
        <AIChecklistItem key={index} content={item} className="" />
      ))}
    </div>
  );
};

/**
 * Validate if a color value is safe to use in CSS
 */
const isValidColor = (color: string): boolean => {
  // Check for valid hex colors
  const hexColorRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
  if (hexColorRegex.test(color)) return true;

  // Check for CSS named colors (basic safe list)
  const safeColors = [
    "red",
    "blue",
    "green",
    "yellow",
    "orange",
    "purple",
    "pink",
    "gray",
    "black",
    "white",
    "brown",
    "cyan",
    "magenta",
    "lime",
    "maroon",
    "navy",
    "olive",
    "silver",
    "teal",
  ];
  return safeColors.includes(color.toLowerCase());
};

// Color Preview Component
export const AIColorPreview: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => {
  // Validate color before applying it
  const safeBackgroundColor = isValidColor(content) ? content : "#cccccc";

  return (
    <span className={cn(className)}>
      <span
        className="inline-block w-4 h-4 rounded border border-gray-300 mr-2"
        style={{ backgroundColor: safeBackgroundColor }}
      />
      <code className="bg-gray-100 px-1 rounded text-sm font-mono">
        {content}
      </code>
    </span>
  );
};

// Table Component
export const AITable: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => {
  // Convert literal \n characters to actual line breaks
  let normalizedContent = content.trim().replace(/\\n/g, "\n");

  // Handle single-line, flattened markdown table where rows are separated by "| |"
  // Example: "| H1 | H2 | | R1C1 | R1C2 | | R2C1 | R2C2 |"
  if (!/\n/.test(normalizedContent) && /\|\s*\|/.test(normalizedContent)) {
    // Insert a line break before each row separator sequence "| |"
    normalizedContent = normalizedContent
      .replace(/\|\s*\|\s*/g, "|\n|")
      .replace(/\n\s*\|\s*\n/g, "\n");
  }

  const lines = normalizedContent.split("\n");

  // Improved parsing that preserves empty cells and handles escaped pipes
  const rows = lines.map((line) => {
    // First, handle escaped pipes by temporarily replacing them
    const tempLine = line.replace(/\\\|/g, "__ESCAPED_PIPE__");

    const cells = tempLine.split("|").map((cell) => {
      // Restore escaped pipes and trim
      return cell.replace(/__ESCAPED_PIPE__/g, "|").trim();
    });

    // Remove empty cells from the beginning and end (common in markdown tables)
    while (cells.length > 0 && cells[0] === "") {
      cells.shift();
    }
    while (cells.length > 0 && cells[cells.length - 1] === "") {
      cells.pop();
    }

    return cells;
  });

  if (rows.length === 0) return null;

  // Filter out markdown separator rows (contains only dashes, pipes, and whitespace)
  const filteredRows = rows.filter((row) => {
    // Check if this row is a markdown separator (like |---|---|---|)
    const rowContent = row.join("").replace(/\s/g, "");
    const isSeparator =
      /^[\|\-\:\s]*$/.test(rowContent) && rowContent.includes("-");
    return !isSeparator;
  });

  if (filteredRows.length === 0) return null;

  const [header, ...dataRows] = filteredRows;

  return (
    <div className={cn(className)}>
      <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
        {header && (
          <thead className="bg-gray-50">
            <tr>
              {header.map((cell, index) => (
                <th
                  key={index}
                  className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b border-gray-200"
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {dataRows.map((row, rowIndex) => (
            <tr key={rowIndex} className="even:bg-gray-50">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200"
                >
                  {/* Render basic markdown such as **bold** inside table cells */}
                  {cell ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: processBasicFormatting(cell),
                      }}
                    />
                  ) : (
                    "\u00A0"
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Unordered List Component
export const AIUnorderedList: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => {
  // If the content already contains <li> tags, extract them to avoid blank bullets
  let items: string[];
  if (/<li\b[^>]*>/i.test(content)) {
    items = Array.from(content.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi))
      .map((m) => (m[1] || "").trim())
      .filter((t) => t.length > 0);
  } else {
    // Normalize: convert embedded <br> into newlines to avoid literal <br> in items
    const normalized = content.replace(/<br\s*\/?>/gi, "\n");
    // Split items by | separator (preferred) or fallback to newlines
    const rawItems = normalized.includes("|")
      ? normalized.split("|")
      : normalized.split(/\n+/);
    items = rawItems
      .map((item) => {
        let cleanedItem = item.trim();
        // Remove leading bullet markers if present (-, *, •)
        cleanedItem = cleanedItem.replace(/^[\-\*•]\s+/, "");
        return cleanedItem;
      })
      .filter((item) => item.length > 0);
  }

  return (
    <ul className={cn(className)}>
      {items.map((item, index) => (
        <li key={index} className="mb-1">
          <span
            dangerouslySetInnerHTML={{ __html: processBasicFormatting(item) }}
          />
        </li>
      ))}
    </ul>
  );
};

// Ordered List Component
export const AIOrderedList: React.FC<AIRichContentProps> = ({
  content,
  className,
}) => {
  // If content already contains <li> tags, extract them
  let items: string[];
  if (/<li\b[^>]*>/i.test(content)) {
    items = Array.from(content.matchAll(/<li\b[^>]*>([\s\S]*?)<\/li>/gi))
      .map((m) => (m[1] || "").trim())
      .filter((t) => t.length > 0);
  } else {
    // Normalize: convert embedded <br> into newlines to avoid literal <br> in items
    const normalized = content.replace(/<br\s*\/?>/gi, "\n");
    // Split items by | separator (preferred) or fallback to newlines
    const rawItems = normalized.includes("|")
      ? normalized.split("|")
      : normalized.split(/\n+/);
    items = rawItems
      .map((item) => {
        let cleanedItem = item.trim();
        // Remove leading numbers with various formats: "1. ", "1) ", "1 ", etc.
        cleanedItem = cleanedItem.replace(/^\d+[\.\)\s]+/, "");
        // Also handle bold tags around numbers
        cleanedItem = cleanedItem.replace(
          /^<(b|strong)>\s*\d+[\.\)\s]+/,
          "<$1>",
        );
        return cleanedItem;
      })
      .filter((item) => item.length > 0);
  }

  return (
    <ol className={cn(className)}>
      {items.map((item, index) => (
        <li key={index} className="mb-1">
          <span
            dangerouslySetInnerHTML={{ __html: processBasicFormatting(item) }}
          />
        </li>
      ))}
    </ol>
  );
};

// Component map for dynamic rendering
export const AI_RICH_CONTENT_COMPONENTS = {
  AIInfoBox,
  AIHighlight,
  AIActionItem,
  AIWarning,
  AICodeBlock,
  AIQuote,
  AICallout,
  AITask,
  AIChecklistItem,
  AIChecklistGroup,
  AIColorPreview,
  AITable,
  AIUnorderedList,
  AIOrderedList,
} as const;
